import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDarkMode } from './DarkModeContext';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

// Generic, workflow-agnostic step-flow diagram: horizontal timeline (step order = x-position),
// swimlanes by owner_role (y-position = row), current step highlighted, gateway/decision steps
// rendered as diamonds. Built generically (not FCA-Quarterly-specific) so a future branching
// workflow (e.g. Payroll) can reuse this unchanged - it only needs `steps` to include an
// `is_gateway`/`branches`-aware entry; FCA Quarterly Reporting itself has no branch, so every
// step here is a plain rectangle in a straight line.
//
// `steps` (as returned by GET /workflows/{id}): [{ key, label, owner_role, is_approval_gate,
// is_gateway, status: 'done'|'current'|'pending', actor_name, completed_at }, ...]

const ROLE_LABELS = {
  system: 'Automated', finance: 'Financial Controller', compliance: 'Compliance',
  coo: 'COO', hr: 'HR', it: 'IT', admin: 'Admin', employee: 'Employee', ceo: 'CEO',
};

// STATUSPALETTE (Marie-Lynne's decision, pending COO approval): same palette as WorkflowList.js's
// StatusPill, scoped to this module only. done -> Completed values, current -> In progress
// values. Steps never carry an "escalated" or "ignored" status in the step model (those are
// instance-level concepts shown in WorkflowList), so only the 3 states below apply here; "pending"
// ("not yet started") uses its own distinct grey-neutral pair per this task's spec, replacing the
// previous ad hoc transparent/theme-grey treatment with the same text+fill+border shape as every
// other state.
const STATUS_COLOURS = {
  done:    { light: { text: '#1A2744', fill: '#FFFFFF', border: '#6A7794' }, dark: { text: '#C6CFDE', fill: '#232C3C', border: '#6B7488' } },
  current: { light: { text: '#12447F', fill: '#D9E7F7', border: '#5A82B4' }, dark: { text: '#9CC2F2', fill: '#0E2749', border: '#4A6EA4' } },
  pending: { light: { text: '#5A626E', fill: '#FFFFFF', border: '#D0D5DD' }, dark: { text: '#8E98A8', fill: '#131c2e', border: '#2f3d5a' } },
};

function StepNode({ step, isDarkMode, nodeRef, renderEvidence }) {
  const colours = (STATUS_COLOURS[step.status] || STATUS_COLOURS.pending)[isDarkMode ? 'dark' : 'light'];
  const border = colours.border;
  const bg = colours.fill;
  const text = colours.text;

  const shape = step.is_gateway ? {
    transform: 'rotate(45deg)',
    borderRadius: '6px',
  } : {
    borderRadius: '10px',
  };

  return (
    <div ref={nodeRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '160px', flexShrink: 0 }}>
      <div style={{
        width: step.is_gateway ? '56px' : '140px',
        height: step.is_gateway ? '56px' : 'auto',
        minHeight: step.is_gateway ? undefined : '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: bg, border: `2px solid ${border}`, color: text,
        padding: step.is_gateway ? 0 : '10px 12px',
        fontSize: '12.5px', fontWeight: 600, textAlign: 'center', lineHeight: 1.3,
        // STATUSPALETTE: the current-step glow now echoes the in-progress border colour instead
        // of the removed gold, so the highlight stays in the same navy/blue family as the fill.
        boxShadow: step.status === 'current' ? `0 0 0 3px ${isDarkMode ? 'rgba(156,194,242,0.25)' : 'rgba(90,130,180,0.25)'}` : 'none',
        ...shape,
      }}>
        <span style={step.is_gateway ? { transform: 'rotate(-45deg)' } : undefined}>
          {step.label}{step.is_approval_gate ? ' ✓' : ''}
        </span>
      </div>
      <div style={{ marginTop: '8px', fontSize: '11px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#888', textAlign: 'center', minHeight: '30px' }}>
        {step.status === 'done' && (
          <>
            <div>{step.actor_name || ROLE_LABELS[step.owner_role] || step.owner_role}</div>
            {step.completed_at && <div>{toDisplayDate(step.completed_at).toLocaleDateString()}</div>}
            {/* PENSIONFORM: structured values captured by a form step (today: Pension's NEST
                confirmation) shown right under the actor/date, so the audit record is readable
                in place, not just retrievable via the API. */}
            {step.form_data && step.form_data.nest_reference && (
              <div>NEST: {step.form_data.nest_reference}</div>
            )}
            {step.form_data && step.form_data.submission_date && (
              <div>Submitted {toDisplayDate(step.form_data.submission_date).toLocaleDateString()}</div>
            )}
            {/* PAYROLLFORM: compact one-line summary of the COO's changes confirmation - the
                full text lives in WorkflowList's "Captured form data" card. */}
            {step.form_data && typeof step.form_data.no_changes === 'boolean' && (
              <div>
                {step.form_data.no_changes
                  ? 'No changes this month'
                  /* PAYROLLROWS: the three categories are arrays of rows now. The old truthiness
                     filter would have listed a category with ZERO rows, since [] is truthy in JS -
                     so the count is what decides, and each category shows how many rows it holds. */
                  : ['new_joiners', 'leavers', 'salary_changes', 'bonus_premium']
                      .filter(k => Array.isArray(step.form_data[k]) && step.form_data[k].length > 0)
                      .map(k => `${({ new_joiners: 'New joiners', leavers: 'Leavers', salary_changes: 'Salary changes', bonus_premium: 'Bonus or premium' })[k]} (${step.form_data[k].length})`)
                      .join(', ')}
              </div>
            )}
            {step.form_data && step.form_data.processing_notes && (
              <div>Notes recorded</div>
            )}
            {/* MACOOAPPROVE: the uploaded proof was stored (step_state.evidence_url, returned
                by the API since the step-completion work) but never rendered anywhere - the
                gap the workflow audit flagged. A completed step with evidence now links to it,
                generically for ANY workflow type with an upload step, so a submission document
                is retrievable from the UI, not just present in the database.

                STEPEVIDENCE: the raw `<a href={step.evidence_url}>` that sat here pointed at the
                SharePoint webUrl. The click LEFT the portal, carried no bearer token, and opened
                only for someone with direct SharePoint access to the Finance site - so it failed
                silently for the very people this link was shown to. It is now RENDERED BY THE
                CALLER through `renderEvidence`, which supplies a token-scoped control.

                THIS FILE STAYS LAYOUT-ONLY, per its standing rule (DOM-measurement based, built
                on top of and never modified): the prop is OPTIONAL and additive, and a caller
                that omits it falls back to the plain, unlinked wording rather than to the old
                tokenless anchor - no caller is broken and no dead link is left behind. */}
            {step.evidence_url && (
              <div onClick={e => e.stopPropagation()}>
                {renderEvidence ? renderEvidence(step) : (
                  <span style={{ color: STATUS_COLOURS.done[isDarkMode ? 'dark' : 'light'].text, fontWeight: 600 }}>
                    Evidence attached
                  </span>
                )}
              </div>
            )}
          </>
        )}
        {step.status === 'current' && (
          // STATUSPALETTE: In progress text colour, matching the node's own text colour.
          <div style={{ color: STATUS_COLOURS.current[isDarkMode ? 'dark' : 'light'].text, fontWeight: 600 }}>In progress</div>
        )}
      </div>
    </div>
  );
}

export default function WorkflowDiagram({ steps, renderEvidence }) {
  const { isDarkMode } = useDarkMode();
  const containerRef = useRef(null);
  const nodeRefs = useRef([]);
  const [lines, setLines] = useState([]);

  nodeRefs.current = [];
  const registerNode = (i) => (el) => { nodeRefs.current[i] = el; };

  // Swimlanes: one row per distinct owner_role, ordered by first appearance in the step
  // sequence (not alphabetically) so "Automated" steps naturally bookend a human-owned flow.
  const lanes = [];
  steps.forEach((s) => { if (!lanes.includes(s.owner_role)) lanes.push(s.owner_role); });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const newLines = [];
    for (let i = 0; i < steps.length - 1; i++) {
      const a = nodeRefs.current[i];
      const b = nodeRefs.current[i + 1];
      if (!a || !b) continue;
      const ra = a.getBoundingClientRect();
      const rb = b.getBoundingClientRect();
      newLines.push({
        x1: ra.right - containerRect.left + container.scrollLeft,
        y1: ra.top - containerRect.top + container.scrollTop + ra.height / 2,
        x2: rb.left - containerRect.left + container.scrollLeft,
        y2: rb.top - containerRect.top + container.scrollTop + rb.height / 2,
        done: steps[i].status === 'done',
      });
    }
    setLines(newLines);
  }, [steps, isDarkMode]);

  if (!steps || steps.length === 0) {
    return <div style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#888' }}>No steps to display.</div>;
  }

  const laneHeight = 110;

  return (
    <div ref={containerRef} style={{ position: 'relative', overflowX: 'auto', padding: '16px 8px' }}>
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
        {lines.map((l, i) => (
          // STATUSPALETTE: a "done" connector now uses the Completed border colour instead of
          // the removed dark green, so it reads as the same status family as the node it leads
          // into rather than a third, unrelated colour.
          <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
            stroke={l.done ? STATUS_COLOURS.done[isDarkMode ? 'dark' : 'light'].border : (isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd')}
            strokeWidth="2" markerEnd="url(#arrowhead)" />
        ))}
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <path d="M0,0 L8,4 L0,8 Z" fill={isDarkMode ? 'rgba(255,255,255,0.35)' : '#98a2b3'} />
          </marker>
        </defs>
      </svg>
      <div style={{ display: 'flex', minWidth: 'max-content' }}>
        {/* Swimlane labels */}
        <div style={{ display: 'flex', flexDirection: 'column', marginRight: '12px', flexShrink: 0 }}>
          {lanes.map((role) => (
            <div key={role} style={{
              height: `${laneHeight}px`, display: 'flex', alignItems: 'center',
              fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
              color: isDarkMode ? 'rgba(255,255,255,0.45)' : '#98a2b3', width: '120px',
            }}>
              {ROLE_LABELS[role] || role}
            </div>
          ))}
        </div>
        {/* Steps, each placed in its lane's row */}
        <div style={{ position: 'relative', flex: 1 }}>
          {lanes.map((role, laneIdx) => (
            <div key={role} style={{
              position: 'absolute', top: `${laneIdx * laneHeight}px`, left: 0, right: 0,
              height: `${laneHeight}px`, display: 'flex', alignItems: 'center', gap: '48px',
              borderTop: laneIdx > 0 ? `1px dashed ${isDarkMode ? 'rgba(255,255,255,0.08)' : '#eef0f2'}` : 'none',
            }}>
              {steps.map((step, i) => (
                step.owner_role === role
                  ? <div key={step.key} style={{ marginLeft: i === 0 ? 0 : `${i * 188}px`, position: 'absolute', left: 0 }}>
                      <StepNode step={step} isDarkMode={isDarkMode} nodeRef={registerNode(i)} renderEvidence={renderEvidence} />
                    </div>
                  : null
              ))}
            </div>
          ))}
          <div style={{ height: `${lanes.length * laneHeight}px` }} />
        </div>
      </div>
    </div>
  );
}
